import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Shield, Smartphone, Mail, Key, Check, X, QrCode, Copy, Eye, EyeOff, AlertTriangle, Info, RefreshCw, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import { HelpIcon } from "@/components/HelpIcon";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

export default function TwoFactorSettings() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();

  const [totpEnabled, setTotpEnabled] = useState(false);
  const [emailOtpEnabled, setEmailOtpEnabled] = useState(false);
  const [preferredMethod, setPreferredMethod] = useState("totp");

  // Sync from real user profile
  useEffect(() => {
    if (user) {
      setTotpEnabled(user.totp_enabled || false);
      setEmailOtpEnabled(user.email_otp_enabled || false);
      setPreferredMethod(user.preferred_2fa_method || "totp");
    }
  }, [user]);

  // Setup dialogs
  const [setupTotpOpen, setSetupTotpOpen] = useState(false);
  const [setupEmailOtpOpen, setSetupEmailOtpOpen] = useState(false);
  const [recoveryCodesOpen, setRecoveryCodesOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablingMethod, setDisablingMethod] = useState(null);

  // Setup state
  const [setupStep, setSetupStep] = useState(1);
  const [otpValue, setOtpValue] = useState("");
  const [copiedCode, setCopiedCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // Real TOTP setup data from backend
  const [totpSetupData, setTotpSetupData] = useState(null);

  // Legacy field (kept for UI compatibility)
  const showRecoveryCodes = recoveryCodes.length > 0;

  const handleSetupTotp = async () => {
    setLoading(true);
    try {
      const data = await authApi.setupTOTP();
      setTotpSetupData(data);
      setSetupStep(1);
      setOtpValue("");
      setSetupTotpOpen(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupEmailOtp = () => {
    setSetupStep(1);
    setOtpValue("");
    setSetupEmailOtpOpen(true);
  };

  const handleVerifyTotp = async () => {
    if (otpValue.length !== 6 || !totpSetupData) return;
    setLoading(true);
    try {
      const result = await authApi.confirmTOTP(totpSetupData.secret, otpValue);
      setRecoveryCodes(result.codes || []);
      setSetupStep(3);
      setTotpEnabled(true);
      await refreshUser();
      toast.success("Authenticator app enabled!");
    } catch (err) {
      toast.error(err.message || "Invalid code — please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (otpValue.length !== 6) return;
    setLoading(true);
    try {
      await authApi.enableEmailOTP();
      setSetupStep(3);
      setEmailOtpEnabled(true);
      await refreshUser();
      toast.success("Email OTP enabled!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = (method) => {
    setDisablingMethod(method);
    setDisableDialogOpen(true);
  };

  const confirmDisable = async () => {
    setLoading(true);
    try {
      if (disablingMethod === "totp") {
        await authApi.disableTOTP();
        setTotpEnabled(false);
      } else {
        await authApi.disableEmailOTP();
        setEmailOtpEnabled(false);
      }
      await refreshUser();
      toast.success(`${disablingMethod === "totp" ? "Authenticator app" : "Email OTP"} disabled`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
      setDisableDialogOpen(false);
      setDisablingMethod(null);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(totpSetupData?.secret || "");
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleRegenerateRecoveryCodes = async () => {
    setLoading(true);
    try {
      const result = await authApi.regenerateRecoveryCodes();
      setRecoveryCodes(result.codes || []);
      setRecoveryCodesOpen(true);
      toast.success("New recovery codes generated");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const hasMfaEnabled = totpEnabled || emailOtpEnabled;

  return (
    <div data-testid="two-factor-settings-page">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="two-factor-title">
            {t("mfa.title", "Two-Factor Authentication")}
          </h1>
          <HelpIcon featureTag="2fa" portalType="customer" />
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {t("mfa.subtitle", "Add an extra layer of security to your account")}
        </p>
      </div>

      {/* Status Overview */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${hasMfaEnabled ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <Shield className={`w-6 h-6 ${hasMfaEnabled ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">
                {hasMfaEnabled ? t("mfa.enabled", "Two-Factor Authentication Enabled") : t("mfa.disabled", "Two-Factor Authentication Disabled")}
              </h2>
              <p className="text-sm text-slate-500">
                {hasMfaEnabled 
                  ? t("mfa.enabled_desc", "Your account is protected with additional verification")
                  : t("mfa.disabled_desc", "Enable 2FA to add extra security to your account")}
              </p>
            </div>
          </div>
          <Badge variant="outline" className={hasMfaEnabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
            {hasMfaEnabled ? t("mfa.secure", "Secured") : t("mfa.at_risk", "At Risk")}
          </Badge>
        </div>
      </div>

      {/* MFA Methods */}
      <div className="space-y-4 mb-6">
        {/* Authenticator App (TOTP) */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="totp-method-card">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{t("mfa.authenticator_app", "Authenticator App")}</h3>
                  {totpEnabled && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{t("label.active", "Active")}</Badge>
                  )}
                  {totpEnabled && preferredMethod === "totp" && (
                    <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{t("mfa.preferred", "Preferred")}</Badge>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-0.5">
                  {t("mfa.totp_desc", "Use an authenticator app like Google Authenticator, Authy, or 1Password")}
                </p>
                {totpEnabled && (
                  <div className="mt-3 flex items-center gap-4">
                    <span className="text-xs text-slate-400">{t("mfa.configured_on", "Configured on:")} Dec 1, 2025</span>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleRegenerateRecoveryCodes} disabled={loading}>
                      <Key className="w-3 h-3 mr-1" /> {t("mfa.regenerate_recovery_codes", "Regenerate Recovery Codes")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {totpEnabled ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => handleDisable("totp")} data-testid="disable-totp-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> {t("mfa.disable", "Disable")}
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={handleSetupTotp} disabled={loading} data-testid="setup-totp-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {t("mfa.setup", "Set Up")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Email OTP */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="email-otp-method-card">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{t("mfa.email_otp", "Email Verification")}</h3>
                  {emailOtpEnabled && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{t("label.active", "Active")}</Badge>
                  )}
                  {emailOtpEnabled && preferredMethod === "email" && (
                    <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{t("mfa.preferred", "Preferred")}</Badge>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-0.5">
                  {t("mfa.email_otp_desc", "Receive a one-time code via email to verify your identity")}
                </p>
                {emailOtpEnabled && (
                  <div className="mt-3">
                    <span className="text-xs text-slate-400">{t("mfa.email_address", "Email:")} u***@example.com</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {emailOtpEnabled ? (
                <Button variant="outline" size="sm" onClick={() => handleDisable("email")} data-testid="disable-email-otp-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> {t("mfa.disable", "Disable")}
                </Button>
              ) : (
                <Button size="sm" onClick={handleSetupEmailOtp} data-testid="setup-email-otp-btn">
                  <Plus className="w-4 h-4 mr-2" /> {t("mfa.setup", "Set Up")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preferred Method Selection */}
      {totpEnabled && emailOtpEnabled && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">{t("mfa.preferred_method", "Preferred Verification Method")}</h3>
          <RadioGroup value={preferredMethod} onValueChange={setPreferredMethod} className="space-y-3">
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="totp" id="pref-totp" />
              <Label htmlFor="pref-totp" className="flex items-center gap-2 cursor-pointer">
                <Smartphone className="w-4 h-4 text-purple-600" />
                {t("mfa.authenticator_app", "Authenticator App")}
              </Label>
            </div>
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="email" id="pref-email" />
              <Label htmlFor="pref-email" className="flex items-center gap-2 cursor-pointer">
                <Mail className="w-4 h-4 text-blue-600" />
                {t("mfa.email_otp", "Email Verification")}
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}

      {/* Security Info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          {t("mfa.security_info", "We recommend enabling at least one 2FA method. Authenticator apps provide the highest security as they work offline and are not susceptible to SIM-swapping attacks.")}
        </AlertDescription>
      </Alert>

      {/* Setup TOTP Dialog */}
      <Dialog open={setupTotpOpen} onOpenChange={setSetupTotpOpen}>
        <DialogContent className="max-w-md" aria-describedby="setup-totp-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" /> {t("mfa.setup_authenticator", "Set Up Authenticator App")}
            </DialogTitle>
            <DialogDescription id="setup-totp-desc">
              {setupStep === 1 && t("mfa.scan_qr_desc", "Scan the QR code with your authenticator app")}
              {setupStep === 2 && t("mfa.verify_code_desc", "Enter the 6-digit code from your app to verify")}
              {setupStep === 3 && t("mfa.setup_complete_desc", "Your authenticator app is now set up!")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {setupStep === 1 && (
              <div className="space-y-4">
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-lg p-6 flex flex-col items-center">
                {/* QR Code - real from backend */}
                  <div className="w-48 h-48 flex items-center justify-center mb-4">
                    {totpSetupData?.uri ? (
                      <QRCodeSVG value={totpSetupData.uri} size={192} level="M" />
                    ) : (
                      <QrCode className="w-32 h-32 text-slate-400" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 text-center">{t("mfa.scan_with_app", "Scan with Google Authenticator, Authy, or similar")}</p>
                </div>
                
                <div className="border-t pt-4">
                  <p className="text-xs text-slate-500 mb-2">{t("mfa.manual_entry", "Or enter this code manually:")}</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-100 px-3 py-2 rounded font-mono text-sm">{totpSetupData?.secret || "..."}</code>
                    <Button variant="outline" size="sm" onClick={copySecret}>
                      {copiedCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {setupStep === 2 && (
              <div className="space-y-4">
                <div className="text-center">
                  <InputOTP maxLength={6} value={otpValue} onChange={setOtpValue} data-testid="totp-setup-otp">
                    <InputOTPGroup className="gap-2 justify-center">
                      {[0, 1, 2, 3, 4, 5].map(i => (
                        <InputOTPSlot key={i} index={i} className="w-11 h-14 text-lg font-mono bg-white border-slate-200" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
            )}

            {setupStep === 3 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{t("mfa.success", "Setup Complete!")}</h4>
                  <p className="text-sm text-slate-500">{t("mfa.totp_success_desc", "Your authenticator app is now linked to your account")}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            {setupStep === 1 && (
              <>
                <Button variant="outline" onClick={() => setSetupTotpOpen(false)}>{t("action.cancel", "Cancel")}</Button>
                <Button onClick={() => setSetupStep(2)}>{t("action.next", "Next")}</Button>
              </>
            )}
            {setupStep === 2 && (
              <>
                <Button variant="outline" onClick={() => setSetupStep(1)}>{t("action.back", "Back")}</Button>
                <Button onClick={handleVerifyTotp} disabled={otpValue.length !== 6 || loading} data-testid="verify-totp-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {t("mfa.verify", "Verify")}
                </Button>
              </>
            )}
            {setupStep === 3 && (
              <Button onClick={() => setSetupTotpOpen(false)}>{t("action.done", "Done")}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Email OTP Dialog */}
      <Dialog open={setupEmailOtpOpen} onOpenChange={setSetupEmailOtpOpen}>
        <DialogContent className="max-w-md" aria-describedby="setup-email-otp-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" /> {t("mfa.setup_email", "Set Up Email Verification")}
            </DialogTitle>
            <DialogDescription id="setup-email-otp-desc">
              {setupStep === 1 && t("mfa.email_verify_desc", "We'll send a verification code to your registered email")}
              {setupStep === 2 && t("mfa.enter_email_code", "Enter the 6-digit code sent to your email")}
              {setupStep === 3 && t("mfa.email_setup_complete", "Email verification is now enabled!")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {setupStep === 1 && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <Label className="text-xs text-slate-500">{t("mfa.verification_email", "Verification email will be sent to:")}</Label>
                  <p className="font-medium text-slate-900 mt-1">user@example.com</p>
                </div>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {t("mfa.email_code_info", "The code will expire in 10 minutes. Make sure to check your spam folder.")}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {setupStep === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 text-center">{t("mfa.code_sent", "Code sent to")} <span className="font-medium">u***@example.com</span></p>
                <div className="text-center">
                  <InputOTP maxLength={6} value={otpValue} onChange={setOtpValue} data-testid="email-otp-setup-input">
                    <InputOTPGroup className="gap-2 justify-center">
                      {[0, 1, 2, 3, 4, 5].map(i => (
                        <InputOTPSlot key={i} index={i} className="w-11 h-14 text-lg font-mono bg-white border-slate-200" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="text-center">
                  <Button variant="ghost" size="sm" className="text-xs">
                    <RefreshCw className="w-3 h-3 mr-1" /> {t("mfa.resend_code", "Resend Code")}
                  </Button>
                </div>
              </div>
            )}

            {setupStep === 3 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{t("mfa.success", "Setup Complete!")}</h4>
                  <p className="text-sm text-slate-500">{t("mfa.email_success_desc", "Email verification is now enabled for your account")}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            {setupStep === 1 && (
              <>
                <Button variant="outline" onClick={() => setSetupEmailOtpOpen(false)}>{t("action.cancel", "Cancel")}</Button>
                <Button onClick={() => setSetupStep(2)} data-testid="send-email-code-btn">
                  {t("mfa.send_code", "Send Code")}
                </Button>
              </>
            )}
            {setupStep === 2 && (
              <>
                <Button variant="outline" onClick={() => setSetupStep(1)}>{t("action.back", "Back")}</Button>
                <Button onClick={handleVerifyEmailOtp} disabled={otpValue.length !== 6} data-testid="verify-email-otp-btn">
                  {t("mfa.verify", "Verify")}
                </Button>
              </>
            )}
            {setupStep === 3 && (
              <Button onClick={() => setSetupEmailOtpOpen(false)}>{t("action.done", "Done")}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recovery Codes Dialog */}
      <Dialog open={recoveryCodesOpen} onOpenChange={setRecoveryCodesOpen}>
        <DialogContent className="max-w-md" aria-describedby="recovery-codes-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" /> {t("mfa.recovery_codes", "Recovery Codes")}
            </DialogTitle>
            <DialogDescription id="recovery-codes-desc">
              {t("mfa.recovery_codes_desc", "Use these codes if you lose access to your authenticator app")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Alert className="mb-4 bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700 text-xs">
                {t("mfa.recovery_codes_warning", "Store these codes securely. Each code can only be used once.")}
              </AlertDescription>
            </Alert>

            <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm">
              {recoveryCodes.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {recoveryCodes.map((code, i) => (
                    <div key={i} className="bg-white px-2 py-1 rounded border border-slate-200 text-xs">{code}</div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-500 text-xs">Codes are shown only when generated or regenerated.</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={copyRecoveryCodes}>
              {copiedCode ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {t("action.copy", "Copy")}
            </Button>
            <Button onClick={() => setRecoveryCodesOpen(false)}>{t("action.close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="disable-mfa-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> {t("mfa.disable_title", "Disable Two-Factor Authentication?")}
            </DialogTitle>
            <DialogDescription id="disable-mfa-desc">
              {t("mfa.disable_warning", "This will reduce the security of your account. Are you sure you want to continue?")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700 text-xs">
                {disablingMethod === "totp" 
                  ? t("mfa.disable_totp_warning", "Your account will no longer be protected by your authenticator app.")
                  : t("mfa.disable_email_warning", "You will no longer receive verification codes via email.")}
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableDialogOpen(false)}>{t("action.cancel", "Cancel")}</Button>
            <Button variant="destructive" onClick={confirmDisable} data-testid="confirm-disable-mfa-btn">
              {t("mfa.disable", "Disable")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
